import React, {
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Typo from '../../atom/Typo';
import { ChevronUp } from 'lucide-react';
import { cn } from '@/src/utils';

export interface SectionCardProps extends HTMLAttributes<HTMLDivElement> {
  cardTitle?: string | ReactNode;
  rightAccessary?: ReactNode;
  contentProps?: HTMLAttributes<HTMLDivElement>;
  defaultOpen?: boolean;
  isCollapsible?: boolean;
}

const SectionCard = forwardRef<
  HTMLDivElement,
  PropsWithChildren<SectionCardProps>
>(
  (
    {
      cardTitle,
      rightAccessary,
      children,
      contentProps,
      className,
      defaultOpen = true,
      isCollapsible = false,
      ...props
    },
    ref,
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [defaultHeight, setDefaultHeight] = useState(0);
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const renderCardTitle = () => {
      if (typeof cardTitle === 'string') {
        return <Typo typoType="b18sb">{cardTitle}</Typo>;
      }

      return cardTitle;
    };

    useLayoutEffect(() => {
      if (isCollapsible) {
        setTimeout(() => {
          setDefaultHeight(
            contentRef.current?.getBoundingClientRect().height || 0,
          );
        }, 100);
      }
    }, [isCollapsible]);

    return (
      <article
        ref={ref}
        className={cn('rounded-2xl bg-white p-4 shadow-sectionCard', className)}
        {...props}
      >
        {cardTitle && (
          <div
            className="flex min-h-[48px] items-center rounded-lg bg-gray0 px-4 py-0"
            onClick={() => {
              if (isCollapsible) {
                setIsOpen(prev => !prev);
              }
            }}
            style={{ cursor: isCollapsible ? 'pointer' : 'default' }}
          >
            {cardTitle && renderCardTitle()}
            {rightAccessary && <div className="ml-auto">{rightAccessary}</div>}
            {isCollapsible && (
              <button
                className="ml-auto"
                style={{ transform: !isOpen ? 'rotate(180deg)' : 'none' }}
                type="button"
              >
                <ChevronUp />
              </button>
            )}
          </div>
        )}

        {children && (
          <section
            style={{
              height: isCollapsible
                ? isOpen
                  ? `${defaultHeight}px`
                  : `0px`
                : 'auto',
              overflow: 'hidden',
            }}
            className="transition-all duration-300"
          >
            <div ref={contentRef} className="px-4 py-6" {...contentProps}>
              {children}
            </div>
          </section>
        )}
      </article>
    );
  },
);

export default SectionCard;
